import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type {
  MyAlertsResponse,
  NotificationDefaultsResponse,
} from "../../../shared/api-types";
import { authenticatedApi } from "@/lib/api";
import {
  createNotificationSettingsClient,
  defaultsQueryKey,
  MY_ALERTS_QUERY_KEY,
  RD_PICKER_QUERY_KEY,
  SETTINGS_QUERY_OPTIONS,
  type DefaultsChange,
  type DefaultsTarget,
  type MyAlertsSave,
  type NotificationSettingsClient,
} from "@/lib/notification-settings-client";

// The Notification settings queries (NO-834), with SETTINGS_QUERY_OPTIONS:
// read fresh each time the page opens, never refreshed under an open draft.

function useNotificationSettingsClient(): NotificationSettingsClient {
  const { getToken } = useAuth();

  return useMemo(
    () =>
      createNotificationSettingsClient(<T>(path: string, init?: RequestInit) =>
        authenticatedApi<T>(getToken, path, init)
      ),
    [getToken]
  );
}

export function useNotificationDefaults(target: DefaultsTarget | null) {
  const client = useNotificationSettingsClient();

  return useQuery({
    queryKey: target ? defaultsQueryKey(target) : ["notification-settings", "none"],
    queryFn: () => client.read(target as DefaultsTarget),
    enabled: target !== null,
    ...SETTINGS_QUERY_OPTIONS,
  });
}

export function useNotificationDefaultsRds(enabled: boolean) {
  const client = useNotificationSettingsClient();

  return useQuery({
    queryKey: RD_PICKER_QUERY_KEY,
    queryFn: () => client.listRds(),
    enabled,
    ...SETTINGS_QUERY_OPTIONS,
  });
}

export function useSaveNotificationDefaults(target: DefaultsTarget) {
  const client = useNotificationSettingsClient();
  const queryClient = useQueryClient();

  return {
    mutation: useMutation({
      mutationFn: (changes: DefaultsChange[]) => client.save(target, changes),
    }),
    /** Puts a save's (or a conflict's) answer where the editor reads it. */
    replace(view: NotificationDefaultsResponse): void {
      queryClient.setQueryData(defaultsQueryKey(target), view);
    },
  };
}

export function useMyAlerts(enabled: boolean) {
  const client = useNotificationSettingsClient();

  return useQuery({
    queryKey: MY_ALERTS_QUERY_KEY,
    queryFn: () => client.readMyAlerts(),
    enabled,
    ...SETTINGS_QUERY_OPTIONS,
  });
}

export function useSaveMyAlerts() {
  const client = useNotificationSettingsClient();
  const queryClient = useQueryClient();

  return {
    mutation: useMutation({
      mutationFn: (save: MyAlertsSave) => client.saveMyAlerts(save),
    }),
    /** Puts a save's (or a conflict's) answer where the editor reads it. */
    replace(view: MyAlertsResponse): void {
      queryClient.setQueryData(MY_ALERTS_QUERY_KEY, view);
    },
  };
}
